import React, { ReactElement, useEffect } from 'react';
import {
  Checkbox,
  CheckboxGroup,
  Col,
  DatePicker,
  FormGroup,
  Heading,
  Input,
  Label,
  Row,
  Select,
  Text,
  Textarea,
} from 'idsk-react-components';
import ClassType from '../../../model/pripomienka/ClassType';
import SaveAdminPripomienka from '../../../model/pripomienka/SaveAdminPripomienka';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import ZasadnaFormComponent from '../../AddPripomienka/component/ZasadnaFormComponent';
import { povahaPripomienkyArray } from '../../../model/pripomienka/PovahaPripomienky';
import './udajePripomienkyFormSection.scss';

interface Props {
  classType: ClassType;
  getFieldErrorMessage: (fieldName: string) => string;
  onUpdateField: SafeParameterSetter<SaveAdminPripomienka>;
  pripomienka: SaveAdminPripomienka;
}

const UdajePripomienkyFormSection = ({
  classType,
  getFieldErrorMessage,
  onUpdateField,
  pripomienka,
}: Props): ReactElement => {
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 10);

  useEffect(() => {
    if (pripomienka.ine === 'Celému materiálu') {
      onUpdateField('celyMaterial', true);
      onUpdateField('ine', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCelyMaterialClick = (checked: boolean): void => {
    onUpdateField('celyMaterial', checked);

    if (checked) {
      onUpdateField('clanok', '');
      onUpdateField('paragraf', '');
      onUpdateField('odsek', '');
      onUpdateField('ine', '');
    }
  };

  const labelClassName: string = pripomienka.celyMaterial ? 'pripomienkak-label-disabled' : null;

  return (
    <section className="add-pripomienka-admin__udaje-pripomienky-form-section">
      <Row className="row-heading">
        <Col>
          <Heading size="m">ÚDAJE PRIPOMIENKY</Heading>
        </Col>
      </Row>
      <FormGroup
        error={!!getFieldErrorMessage('datumVytvorenia')}
      >
        <Label htmlFor="datumVytvorenia"> Dátum vytvorenia</Label>
        <DatePicker
          id="datumVytvorenia"
          error={getFieldErrorMessage('datumVytvorenia')}
          width="one-quarter"
          value={pripomienka.datumVytvorenia}
          onChange={(data) => onUpdateField('datumVytvorenia', data == null ? new Date() : data)}
          minDate={minDate}
          maxDate={new Date()}
        />
      </FormGroup>
      <FormGroup error={!!getFieldErrorMessage('pripomienkaK')}>
        <Text className="pripomienka-k">Pripomienka k</Text>
        <span className="idsk-hint">Zadajte označenie ustanovenia, ktorého sa pripomienka
          týka. Vyplnenie minimálne jednej z možností je povinné.
        </span>
        <Text className="idsk-error-message">{getFieldErrorMessage('pripomienkaK')}</Text>
      </FormGroup>
      <Row>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('clanok')}>
              <Label className={labelClassName} htmlFor="clanok">Článku</Label>
              <Input
                id="clanok"
                rows={1}
                type="text"
                maxLength="20"
                name="clanok"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.clanok}
                autoComplete="off"
                error={getFieldErrorMessage('clanok')}
                onChange={({ target }) => onUpdateField('clanok', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('paragraf')}>
              <Label className={labelClassName} htmlFor="paragraf">Paragrafu</Label>
              <Input
                id="paragraf"
                rows={1}
                type="text"
                maxLength="20"
                name="paragraf"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.paragraf}
                autoComplete="off"
                error={getFieldErrorMessage('paragraf')}
                onChange={({ target }) => onUpdateField('paragraf', target.value)}
              />
            </FormGroup>
          </Col>
          <Col width="one-third">
            <FormGroup error={!!getFieldErrorMessage('odsek')}>
              <Label className={labelClassName} htmlFor="odsek">Odseku</Label>
              <Input
                id="odsek"
                rows={1}
                type="text"
                maxLength="20"
                autoComplete="off"
                name="odsek"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.odsek}
                error={getFieldErrorMessage('odsek')}
                onChange={({ target }) => onUpdateField('odsek', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
        <Col width="one-half" className="fields-column">
          <Col width="one-third">
            <CheckboxGroup className="add-pripomienka-checkbox-wrapper">
              <Checkbox
                className="add-pripomienka-checkbox"
                id="celyMaterial"
                onClick={({ target }) => onCelyMaterialClick(target.checked)}
                defaultChecked={pripomienka.ine === 'Celému materiálu'}
              >
                Celému materiálu
              </Checkbox>
            </CheckboxGroup>
          </Col>
          <Col width="two-thirds">
            <FormGroup error={!!getFieldErrorMessage('ine')}>
              <Label className={labelClassName} htmlFor="ine">Iné</Label>
              <Input
                id="ine"
                rows={1}
                maxLength="120"
                name="ine"
                type="text"
                autoComplete="off"
                className="pripomienkak-input"
                disabled={pripomienka.celyMaterial}
                value={pripomienka.ine}
                error={getFieldErrorMessage('ine')}
                onChange={({ target }) => onUpdateField('ine', target.value)}
              />
            </FormGroup>
          </Col>
        </Col>
      </Row>
      <FormGroup error={!!getFieldErrorMessage('text')}>
        <Label htmlFor="text">Text pripomienky</Label>
        <Textarea
          id="text"
          rows={15}
          maxLength={65000}
          name="text"
          error={(getFieldErrorMessage('text'))}
          onChange={({ target }) => onUpdateField('text', target.value)}
          defaultValue={pripomienka.text}
        />
      </FormGroup>
      <FormGroup>
        <ZasadnaFormComponent
          classType={classType}
          updateField={onUpdateField}
          width="one-quarter"
          isZasadna={pripomienka.zasadna}
          error={getFieldErrorMessage('zasadna')}
        />
      </FormGroup>

      {classType === ClassType.HROMADNA_PRIPOMIENKA && (
        <FormGroup>
          <Label htmlFor="povahaPripomienky">Povaha pripomienky</Label>
          <Select
            id="povahaPripomienky"
            width="one-quarter"
            onChange={({ target }) => onUpdateField('povahaPripomienky', target.value)}
            defaultValue={pripomienka.povahaPripomienky}
          > {povahaPripomienkyArray.map(povaha => (
            <option
              key={povaha.toString()}
              value={povaha}
            >{povaha}
            </option>
          ))}
          </Select>
        </FormGroup>
      )}
      <FormGroup>
        <Label htmlFor="odoslana">Odoslaná</Label>
        <Select
          id="odoslana"
          width="one-quarter"
          onChange={({ target }) => onUpdateField('odoslana', target.value === 'Áno')}
          defaultValue={pripomienka.odoslana ? 'Áno' : 'Nie'}
        >
          <option key="Nie" value="Nie">Nie</option>
          <option key="Áno" value="Áno">Áno</option>
        </Select>
      </FormGroup>
    </section>
  );
};

export default UdajePripomienkyFormSection;
